/**
 * B6 (2.1.0) — gesture arbitration for the bottom sheet.
 *
 * G's report: "on doit pouvoir fermer la bottom sheet en swippant depuis toute
 * la bottom sheet, pas uniquement depuis le haut". Dragging from anywhere means
 * the sheet and its scrollable content now compete for the same vertical
 * gesture, so something has to referee: a downward pull with the content
 * already at the top dismisses the sheet, anything else is left to the
 * browser's native scrolling. These helpers are pure so that arbitration is
 * unit-tested rather than eyeballed on a device.
 */

/** Movement (px) before a gesture is claimed — below this a tap stays a tap. */
export const SHEET_DRAG_ENGAGE_PX = 8;

/** Pull far enough and the sheet closes even from a standstill. */
export const SHEET_DISMISS_DISTANCE_PX = 90;

/** …or flick it: px per ms (0.6 ≈ 600 px/s, the old framer threshold). */
export const SHEET_DISMISS_VELOCITY = 0.6;

/** A flick still needs *some* travel, so a jittery tap can't close the sheet. */
export const SHEET_FLICK_MIN_DISTANCE_PX = 20;

export type SheetGestureVerdict =
  /** Claim the gesture: move the sheet with the finger. */
  | 'drag'
  /** Leave it to the browser (the content scrolls, or the user pulled up). */
  | 'scroll'
  /** Too early to tell — keep watching. */
  | 'wait';

/**
 * @param dy        pointer travel since pointerdown (positive = downward)
 * @param scrollTop scrollTop of the sheet's scroller, or 0 when the gesture
 *                  started outside it (grabber, title) — those always drag
 * @param engagePx  travel needed to claim the gesture
 */
export function sheetGestureVerdict(
  dy: number,
  scrollTop: number,
  engagePx: number = SHEET_DRAG_ENGAGE_PX,
): SheetGestureVerdict {
  // Content scrolled away from the top: the user is reading, not dismissing.
  if (scrollTop > 0) return 'scroll';
  if (dy >= engagePx) return 'drag';
  if (dy <= -engagePx) return 'scroll';
  return 'wait';
}

/** Distance-or-velocity dismiss test, applied on pointerup. */
export function shouldDismissSheet(dy: number, velocity: number): boolean {
  if (dy >= SHEET_DISMISS_DISTANCE_PX) return true;
  return dy >= SHEET_FLICK_MIN_DISTANCE_PX && velocity >= SHEET_DISMISS_VELOCITY;
}
